/**
 * Server-paginated candidates list (contacts with affiliate role that are not
 * yet confirmed affiliates). Uses page/page_size pagination via
 * useAffiliateCandidates. Mirrors AffiliateActiveListSection structure.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users } from 'lucide-react';
import { Button, Card, CardBody, Skeleton } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, Pagination } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { useAffiliateCandidates } from '@/api/affiliate';
import { paginationView } from '@/pages/customers/pagination-summary';
import { buildCandidateColumns } from '../list-configs/affiliate-column-definitions';
import { AffiliateListToolbar } from './affiliate-list-toolbar';

const PAGE_SIZE = 20;

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

export function AffiliateCandidatesListSection() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [rawQuery, setRawQuery] = useState('');
  const query = useDebouncedValue(rawQuery, 400);

  const { data, isLoading, isError, refetch, isFetching } = useAffiliateCandidates({
    page,
    page_size: PAGE_SIZE,
    ...(query ? { q: query } : {}),
  });

  const rows = data?.results ?? [];
  const pagination = data?.pagination;
  const total = pagination?.total ?? rows.length;
  const pageCount = pagination?.total_pages ?? 1;

  const columns = useMemo(() => buildCandidateColumns({ navigate }), [navigate]);

  const [visible, setVisible] = useState<Record<string, boolean>>(
    () => Object.fromEntries(columns.map((c) => [c.id as string, true])),
  );

  const visibleColumns = useMemo(
    () => columns.filter((c) => visible[c.id as string] !== false),
    [columns, visible],
  );

  const onSearch = (value: string) => {
    setRawQuery(value);
    setPage(1);
  };

  const view = paginationView(page, PAGE_SIZE, rows.length, total, pageCount);

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <AffiliateListToolbar
        columns={columns}
        searchPlaceholder="Search candidates…"
        query={rawQuery}
        onQueryChange={onSearch}
        conditions={[]}
        filterOpen={false}
        onFilterOpenChange={() => undefined}
        onApplyFilters={() => undefined}
        visible={visible}
        onToggleColumn={(id, checked) => setVisible((prev) => ({ ...prev, [id]: checked }))}
      />

      {isError ? (
        <Alert
          tone="danger"
          title="Failed to load candidates"
          actions={
            <Button size="sm" variant="ghost" onClick={() => refetch()}>
              Retry
            </Button>
          }
        />
      ) : isLoading ? (
        <Card flat>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} style={{ height: 24 }} />
            ))}
          </CardBody>
        </Card>
      ) : (
        <DataTable
          columns={visibleColumns}
          data={rows}
          getRowId={(r) => String(r.customer_contact_id)}
          emptyState={
            <EmptyState
              icon={<Users size={24} />}
              title="No candidates found"
              description="Contacts with the affiliate role will appear here."
            />
          }
          footer={
            <Pagination
              page={page}
              pageCount={view.pageCount}
              onPageChange={setPage}
              summary={`${view.summary}${isFetching ? ' …' : ''}`}
            />
          }
        />
      )}
    </div>
  );
}
